import { useState, useRef } from 'react'
import { motion, useSpring } from 'framer-motion'
import whyBg from '@/assets/images/whycodefiedbg.webp'
import productSvg from '@/assets/images/product.webp'
import handSvg from '@/assets/images/hand.svg'
import waveSvg from '@/assets/images/wave.webp'

const stats = [
  { value: '20+', label: 'Products & Platforms\nDelivered', color: '#D4575B' },
  { value: '6+', label: 'Countries Represented', color: '#5AB1E8' },
  { value: '3', label: 'Integrated Service Lines', color: '#EAC763' },
  { value: '1', label: 'Accountable Team', color: '#22C55E' },
]

const cards = [
  {
    id: 1,
    title: 'Product-First Thinking',
    description:
      'We build like product owners — focused on usability, scalability, and real impact.',
  },
  {
    id: 2,
    title: 'End-to-End Ownership.',
    description: 'From idea to scale, we build and evolve your product with full accountability.',
  },
  {
    id: 3,
    title: 'Scalable by Design.',
    description: 'Systems and architecture built to support growth from day one.',
  },
]

function WaveCard({ card, delay }: { card: (typeof cards)[0]; delay: number }) {
  const cardRef = useRef<HTMLDivElement>(null)
  const springConfig = { stiffness: 120, damping: 20, mass: 0.8 }
  const x = useSpring(0, springConfig)
  const y = useSpring(0, springConfig)

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (window.innerWidth < 1024) return
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const cx = e.clientX - rect.left - rect.width / 2
    const cy = e.clientY - rect.top - rect.height / 2
    x.set((cx / rect.width) * -24)
    y.set((cy / rect.height) * -16)
  }

  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="flex flex-col rounded-[10px] overflow-hidden w-full max-w-[398px] h-auto xl:h-[538px] cursor-default"
    >
      <div
        className="relative w-full h-[220px] sm:h-[280px] md:h-[340px] xl:h-[418px] overflow-hidden rounded-[10px]"
        style={{ background: '#000824' }}
      >
        <motion.img
          src={productSvg}
          alt=""
          style={{ x, y }}
          className="absolute inset-0 w-full h-full object-cover scale-110 pointer-events-none select-none"
          draggable={false}
        />
      </div>
      <div className="py-4 flex flex-col gap-2">
        <h3 className="font-sans text-[17px] sm:text-[18px] xl:text-[24px] font-medium text-white leading-[31px] tracking-normal">
          {card.title}
        </h3>
        <p className="font-sans text-[13px] xl:text-[16px] text-[#979AC0] leading-[31px] tracking-normal">
          {card.description}
        </p>
      </div>
    </motion.div>
  )
}

function ParallaxCard({
  card,
  delay,
  src,
}: {
  card: (typeof cards)[0]
  delay: number
  src: string
}) {
  const cardRef = useRef<HTMLDivElement>(null)
  const springConfig = { stiffness: 120, damping: 20, mass: 0.8 }
  const x = useSpring(0, springConfig)
  const y = useSpring(0, springConfig)

  function handleMouseMove(e: React.MouseEvent<HTMLDivElement>) {
    if (window.innerWidth < 1024) return
    const rect = cardRef.current?.getBoundingClientRect()
    if (!rect) return
    const cx = e.clientX - rect.left - rect.width / 2
    const cy = e.clientY - rect.top - rect.height / 2
    x.set((cx / rect.width) * 24)
    y.set((cy / rect.height) * 16)
  }

  function handleMouseLeave() {
    x.set(0)
    y.set(0)
  }

  return (
    <motion.div
      ref={cardRef}
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay }}
      onMouseMove={handleMouseMove}
      onMouseLeave={handleMouseLeave}
      className="flex flex-col rounded-[10px] overflow-hidden w-full max-w-[398px] h-auto xl:h-[538px] cursor-default"
    >
      <div
        className="relative w-full h-[220px] sm:h-[280px] md:h-[340px] xl:h-[418px] overflow-hidden rounded-[10px]"
        style={{ background: '#000824' }}
      >
        <motion.img
          src={src}
          alt=""
          style={{ x, y }}
          className="absolute inset-0 w-full h-full object-contain scale-110 pointer-events-none select-none top-[10px] lg:top-[70px]"
          draggable={false}
        />
      </div>
      <div className="py-4 flex flex-col gap-2">
        <h3 className="font-sans text-[17px] sm:text-[18px] xl:text-[24px] font-medium text-white leading-[31px] tracking-normal">
          {card.title}
        </h3>
        <p className="font-sans text-[13px] xl:text-[16px] text-[#979AC0] leading-[31px] tracking-normal">
          {card.description}
        </p>
      </div>
    </motion.div>
  )
}

export default function WhyCodefied() {
  const [activeStat, setActiveStat] = useState<number>(0)

  return (
    <section
      id="why-codefied"
      className="relative w-full overflow-hidden pt-10 sm:pt-14 xl:pt-[120px] pb-[60px] mt-[60px] sm:mt-[80px] xl:mt-[120px]"
      style={{
        backgroundImage: `url(${whyBg})`,
        backgroundSize: 'cover',
        backgroundPosition: 'center',
        backgroundRepeat: 'no-repeat',
      }}
    >
      <div className="container-site relative z-10">
        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-[48px]"
        >
          <h2 className="font-sans font-medium text-white tracking-[-1px] leading-[40px] xl:leading-[55px] text-[26px] sm:text-[34px] md:text-[42px] lg:text-[48px]">
            Why Codefied?
            <br />
            <span className="text-[#979AC0]">
              {' '}
              Built to Be Your Technology
              <br className="hidden sm:block" /> Partner, Not Just a Vendor.
            </span>
          </h2>
        </motion.div>

        {/* Stats row */}
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.1 }}
          className="relative mb-[48px] w-full max-w-[1240px] mx-auto h-auto xl:h-[180px]"
        >
          {/* Static top & bottom border lines — on outer div so they always span full height */}
          <div className="absolute top-0 left-0 right-0 h-px bg-white/15 pointer-events-none" />
          <div className="absolute bottom-0 left-0 right-0 h-px bg-white/15 pointer-events-none" />

          {/* Sliding accent line — top */}
          <div
            className="hidden lg:block absolute top-0 h-[2px] pointer-events-none z-10"
            style={{
              width: '30%',
              left: `${activeStat * 25}%`,
              background: `linear-gradient(to right, transparent, ${stats[activeStat].color} 30%, ${stats[activeStat].color} 70%, transparent)`,
              transition: 'left 0.4s cubic-bezier(0.4,0,0.2,1), background 0.3s ease',
            }}
          />
          {/* Sliding accent line — bottom */}
          <div
            className="hidden lg:block absolute bottom-0 h-[2px] pointer-events-none z-10"
            style={{
              width: '30%',
              left: `${activeStat * 25}%`,
              background: `linear-gradient(to right, transparent, ${stats[activeStat].color} 30%, ${stats[activeStat].color} 70%, transparent)`,
              transition: 'left 0.4s cubic-bezier(0.4,0,0.2,1), background 0.3s ease',
            }}
          />

          <div
            className="grid grid-cols-2 lg:flex lg:flex-row lg:justify-between lg:items-center w-full h-full"
            onMouseLeave={() => {
              if (window.innerWidth >= 1024) setActiveStat(0)
            }}
          >
            {stats.map((stat, i) => {
              const isActive = activeStat === i
              const isDimmed = !isActive

              return (
                <div
                  key={i}
                  onMouseEnter={() => {
                    if (window.innerWidth >= 1024) setActiveStat(i)
                  }}
                  onClick={() => setActiveStat(i)}
                  className="flex flex-col gap-1 py-6 px-4 sm:px-6 xl:px-8 cursor-pointer items-center text-center lg:cursor-default"
                >
                  <span
                    className="font-sans text-[28px] sm:text-[32px] xl:text-[48px] font-normal tracking-[-1px] leading-tight"
                    style={{
                      color: isActive
                        ? '#ffffff'
                        : isDimmed
                          ? 'rgba(255,255,255,0.25)'
                          : 'rgba(255,255,255,0.55)',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {stat.value}
                  </span>
                  <span
                    className="font-sans text-[11px] sm:text-[12px] xl:text-[16px] tracking-normal leading-tight"
                    style={{
                      color: isActive
                        ? '#ffffff'
                        : isDimmed
                          ? 'rgba(255,255,255,0.18)'
                          : 'rgba(255,255,255,0.4)',
                      transition: 'color 0.3s ease',
                    }}
                  >
                    {stat.label}
                  </span>
                </div>
              )
            })}
          </div>
        </motion.div>

        {/* Cards */}
        <div className="flex flex-col lg:flex-row justify-between items-center lg:items-start w-full gap-6">
          {cards.map((card, i) => {
            if (card.id === 1) {
              return <WaveCard key={card.id} card={card} delay={0.1 + i * 0.1} />
            }
            if (card.id === 3) {
              return <ParallaxCard key={card.id} card={card} delay={0.1 + i * 0.1} src={waveSvg} />
            }
            return <ParallaxCard key={card.id} card={card} delay={0.1 + i * 0.1} src={handSvg} />
          })}
        </div>
      </div>
    </section>
  )
}
